import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProfileCard from '../ui/ProfileCard';
import CardState from '../ui/CardState';
import ActivityFilters from '../activity/ActivityFilters';
import ActivityList from '../activity/ActivityList';
import { fetchActivity } from '../activity/activityApi';
import { toastError } from '../../../lib/toast';
import '../activity/activity.css';

const WHO = ['all', 'by', 'about'];
const CATEGORIES = ['requests', 'access', 'apps', 'profile', 'admin'];

/**
 * The person's activity timeline: what they did, and what was done to their
 * account. Filters live in the URL (?tab=activity&who=by&category=apps) so a
 * filtered view survives a reload and can be linked.
 */
export default function ActivityTab({ userId, subjectName, isSelf, canReviewApps }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const who = WHO.includes(searchParams.get('who')) ? searchParams.get('who') : 'all';
  const category = CATEGORIES.includes(searchParams.get('category')) ? searchParams.get('category') : null;

  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [state, setState] = useState('loading'); // loading | ok | error
  // A filter change keeps the current list up (dimmed) until the next one
  // arrives. Swapping it for a short skeleton shrank the page, and the scroll
  // container snapped the whole profile back to the top on every chip click.
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const request = useRef(0);
  const loadedOnce = useRef(false);
  const filtersRef = useRef(null);

  useEffect(() => {
    const mine = ++request.current;
    const isRefresh = loadedOnce.current;
    if (isRefresh) setRefreshing(true);
    else setState('loading');
    fetchActivity({ userId, who, category })
      .then((page) => {
        if (mine !== request.current) return;
        setEntries(page.entries || []);
        setCursor(page.next_cursor || null);
        setShowAdmin(!!page.has_admin_activity);
        setState('ok');
        loadedOnce.current = true;
        // A shorter result still has to shorten the page; keep the filter bar
        // (where the user just clicked) in view rather than wherever the
        // scroll clamp lands. 'nearest' is a no-op when it's already visible.
        if (isRefresh) filtersRef.current?.scrollIntoView?.({ block: 'nearest' });
      })
      .catch(() => { if (mine === request.current) setState('error'); })
      .finally(() => { if (mine === request.current) setRefreshing(false); });
  }, [userId, who, category, reloadKey]);

  const setFilter = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || (key === 'who' && value === 'all')) next.delete(key);
    else next.set(key, value);
    // Replace, don't push: each chip click is a refinement, not a page the
    // Back button should step through.
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loadMore = async () => {
    const mine = request.current;
    setLoadingMore(true);
    try {
      const page = await fetchActivity({ userId, who, category, before: cursor });
      if (mine !== request.current) return;
      setEntries((prev) => [...prev, ...(page.entries || [])]);
      setCursor(page.next_cursor || null);
    } catch {
      toastError("Couldn't load more activity.");
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = who !== 'all' || !!category;
  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('who');
    next.delete('category');
    setSearchParams(next, { replace: true });
  };

  return (
    // No visible title: the tab bar already says "Activity" right above. The
    // region keeps the name for screen readers.
    <ProfileCard span role="region" aria-label="Activity">
      <div ref={filtersRef}>
      <ActivityFilters
        who={who}
        category={category}
        onWho={(v) => setFilter('who', v)}
        onCategory={(v) => setFilter('category', v)}
        subjectName={subjectName}
        isSelf={isSelf}
        showAdmin={showAdmin || category === 'admin'}
      />
      </div>

      {/* The height reserve stops the page jumping on a chip click; it only
          applies while a list is (or is about to be) on screen, so an empty or
          error state keeps its natural height. */}
      <div
        className={`act-results${refreshing ? ' is-refreshing' : ''}${refreshing || (state === 'ok' && entries.length > 0) ? ' act-results--hold' : ''}`}
        aria-busy={refreshing ? 'true' : 'false'}
      >

      {state === 'loading' && <CardState kind="loading" label="Loading activity" lines={5} />}

      {state === 'error' && (
        <div className="act-empty">
          <p className="profile-muted profile-info-card__empty">Couldn&apos;t load activity.</p>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
        </div>
      )}

      {state === 'ok' && entries.length === 0 && (
        filtered ? (
          <div className="act-empty">
            <p className="profile-muted profile-info-card__empty">No activity matches these filters.</p>
            <button type="button" className="act-linkbtn" onClick={clearFilters}>Clear filters</button>
          </div>
        ) : (
          <CardState kind="empty">Nothing here yet. Requests, approvals and profile changes will show up here.</CardState>
        )
      )}

      {state === 'ok' && entries.length > 0 && (
        <>
          <ActivityList entries={entries} isSelf={isSelf} canReviewApps={canReviewApps} />
          {cursor ? (
            <div className="act-more">
              <button type="button" className="btn btn-secondary" onClick={loadMore} disabled={loadingMore} aria-busy={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : (
            <p className="act-end">That&apos;s everything.</p>
          )}
        </>
      )}
      </div>
    </ProfileCard>
  );
}
